import React, { useEffect, useState, useRef, useCallback } from 'react'
import { socket } from '../../../config/SocketApi';
import { useSelector } from 'react-redux';
import Peer from 'simple-peer';
import { Button, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIceConfig } from '../../../config/iceConfig';
import { SOCKET_EVENTS } from '../../../config/socketEvents';
import { bindSocketEvents, destroyPeer, destroyPeerCollection, safeSignalPeer, stopMediaStream } from './realtimeUtils';

const ShareScreenHelper = () => {

    const { whiteBoardDimensions } = useSelector((store) => store.screenSharing)
    const { meetingStarted } = useSelector((store) => store.meeting)
    const [studentShareScreenStream, setStudentShareScreenStream] = useState(false)
    const [sessionStarted, setSessionStarted] = useState(false)
    const [socketErrored, setSocketErrored] = useState(false)
    const { t } = useTranslation();
    const [screenSharingDimensions, setScreenSharingDimensions] = useState()
    const [stream, setStream] = useState();
    const [sessionEnded, setSessionEnded] = useState(false);
    const streamRef = useRef();
    const whiteBoardDimensionsRef = useRef(whiteBoardDimensions);
    const videoRef = useRef(null);
    const tutorSreamRef = useRef();
    const tutorPeerRefs = useRef([]);
    const screenSharingDimensionsRef = useRef();
    const senderStudentsPeerRefs = useRef([]);
    const sessionEndedRef = useRef(false);
    const serverHandlerPeerRef = useRef();
    const meetingStartedRef = useRef(false);
    const whiteboardReadyTimersRef = useRef(new Map());
    // const animationFrameRef = useRef();
    const socketErroredRef = useRef(false)
    const studentShareScreenPeerRef = useRef(false)
    const studentVideoRef = useRef();
    const studentStreamRef = useRef();
    const animationFrameRef = useRef();
    const scalFactor = 1;
    function processStream(stream, x, y, width, height) {
        const video = document.createElement('video');
        video.style.display = 'none';
        video.style.width = width + 'px';
        video.style.height = height + 'px';
        video.width = width;
        video.height = height;
        video.srcObject = stream;
        const canvas = document.createElement('canvas');
        canvas.style.display = 'none';
        const context = canvas.getContext('2d');
        const ratio = window.devicePixelRatio || 1;
        canvas.width = width * ratio * scalFactor;
        canvas.height = height * ratio * scalFactor;
        canvas.style.width = width + 'px';
        canvas.style.height = height + 'px';
        context.imageSmoothingEnabled = false
        context.scale(ratio * scalFactor, ratio * scalFactor);
        const capturedStream = canvas.captureStream(10);
        video.onloadedmetadata = () => {
            video.play();
            animationFrameRef.current = requestAnimationFrame(renderFrame);
        };
        let lastTime = 0;
        const interval = 1000 / 10;
        function renderFrame(now) {
            const deltaTime = now - lastTime;
            if (deltaTime > interval) {
                context.clearRect(0, 0, canvas.width, canvas.height)
                context.drawImage(video, x, y, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
                lastTime = now - (deltaTime % interval);
            }
            animationFrameRef.current = requestAnimationFrame(renderFrame);
        }

        return capturedStream; // Adjust the frame rate as needed
    }
    useEffect(() => {
        streamRef.current = stream;
    }, [stream]);

    useEffect(() => {
        whiteBoardDimensionsRef.current = whiteBoardDimensions;
    }, [whiteBoardDimensions]);

    const addConnectedStudent = useCallback((socket_id, options = {}) => {
        const currentWhiteBoardDimensions = whiteBoardDimensionsRef.current;
        const sourceStream = streamRef.current ?? tutorSreamRef.current;
        if (!socket_id || !currentWhiteBoardDimensions || screenSharingDimensionsRef.current) {
            return;
        }
        if (!sourceStream || !sourceStream.active) {
            console.log("[shareScreenHelper] Student requested whiteboard before stream was ready:", socket_id);
            return;
        }
        if (meetingStartedRef.current) {
            socket.emit("meeting-state", JSON.stringify({ state: meetingStartedRef.current, whiteBoardDimensions: currentWhiteBoardDimensions }))
            return
        }
        if (currentWhiteBoardDimensions && !screenSharingDimensionsRef.current) {
            const existingPeer = tutorPeerRefs.current.find((peerEntry) => peerEntry.studentSocketId === socket_id);
            const existingPeerAgeMs = existingPeer?.createdAt ? Date.now() - existingPeer.createdAt : Number.POSITIVE_INFINITY;
            const existingConnectionState = existingPeer?.peer?._pc?.connectionState || existingPeer?.peer?._pc?.iceConnectionState;
            if (existingPeer && !options.force && ['new', 'connecting', 'connected', 'completed'].includes(existingConnectionState)) {
                return;
            }
            if (existingPeer && existingPeerAgeMs < 2000) {
                return;
            }

            const newPeer = new Peer({
                initiator: true,
                trickle: true,
                stream: sourceStream,
                config: getIceConfig()
            });
            newPeer.on("signal", (data) => {
                socket.emit(SOCKET_EVENTS.LEGACY_WHITEBOARD_TUTOR_SIGNAL, { signal: data, tutor_id: socket.id, studentSocketId: socket_id, whiteBoardDimensions: currentWhiteBoardDimensions });
            });
            newPeer.on('error', (err) => {
                // addConnectedStudent(socket_id)
                console.log("the peer error is ", err)
            })
            if (existingPeer) {
                destroyPeer(existingPeer.peer, "shareScreenHelper replace connected student");
                existingPeer.peer = newPeer;
                existingPeer.createdAt = Date.now();
                return;
            }
            tutorPeerRefs.current.push({ peer: newPeer, studentSocketId: socket_id, createdAt: Date.now() });

        }
    }, [])
    const scheduleConnectedStudent = useCallback((socket_id, options = {}) => {
        if (!socket_id) return;
        const existingTimer = whiteboardReadyTimersRef.current.get(socket_id);
        if (existingTimer) clearTimeout(existingTimer);
        const timer = setTimeout(() => {
            whiteboardReadyTimersRef.current.delete(socket_id);
            addConnectedStudent(socket_id, options);
        }, 250);
        whiteboardReadyTimersRef.current.set(socket_id, timer);
    }, [addConnectedStudent]);
    useEffect(() => {
        const handleWhiteBoardStudentSignal = (data) => {
            const signal = data.data;
            const studentSocketId = data.studentSocketId;
            if (tutorPeerRefs.current.length) {
                const studentPeer = tutorPeerRefs.current.find(p => p.studentSocketId === studentSocketId);
                safeSignalPeer(studentPeer?.peer, signal, "shareScreenHelper white-board student");
            }
        };

        const handleStudentShareScreenSignalToTeacher = (data) => {
            try {
                if (data.signal.type === 'offer') {
                    const newPeer = new Peer({
                        initiator: false,
                        trickle: true,
                        config: getIceConfig()
                    });

                    safeSignalPeer(newPeer, data.signal, "shareScreenHelper student-share offer");

                    newPeer.on("stream", (incomingStream) => {
                        const processedStream = processStream(
                            incomingStream,
                            data.shareScreenDimension.x,
                            data.shareScreenDimension.y,
                            data.shareScreenDimension.width,
                            data.shareScreenDimension.height
                        );
                        setStudentShareScreenStream(processedStream);
                        studentStreamRef.current = processedStream;
                    });

                    newPeer.on("signal", (signal) => {
                        socket.emit("teacher-share-screen-signal", { signal, studentSocket: data.studentSocketId });
                    });
                    newPeer.on('error', (err) => {
                        console.log("the peer error is ", err);
                    });
                    studentShareScreenPeerRef.current = newPeer;
                    return;
                }

                safeSignalPeer(studentShareScreenPeerRef.current, data.signal, "shareScreenHelper student-share answer");
            } catch (error) {
                console.log("the error is ", error);
            }
        };

        const handleScreenSharingDimensions = (data) => {
            screenSharingDimensionsRef.current = data;
            setScreenSharingDimensions(data);
            stopMediaStream(streamRef.current);
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };

        const handleClosedSharingStream = () => {
            handleCloseSharingScreen();
        };

        const handleStudentConnectedWhiteBoard = (socket_id) => {
            scheduleConnectedStudent(socket_id);
        };

        const handleStudentReadyForWhiteboard = (data) => {
            scheduleConnectedStudent(data?.studentSocketId, { force: true });
        };

        const handleStudentDisconnect = (socket_id) => {
            const disconnectedStudent = tutorPeerRefs.current.find(p => p.studentSocketId === socket_id);
            if (disconnectedStudent) {
                destroyPeer(disconnectedStudent.peer, "shareScreenHelper student-disconnect");
                tutorPeerRefs.current = tutorPeerRefs.current.filter(p => p.studentSocketId !== socket_id);
            }

            const disconnectedSenderStudent = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === socket_id);
            if (disconnectedSenderStudent) {
                destroyPeer(disconnectedSenderStudent.peer, "shareScreenHelper sender-student-disconnect");
                senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== socket_id);
            }
        };

        const handleWhiteBoardServerEndSession = () => {
            sessionEndedRef.current = true;
            setSessionEnded(true);
        };

        const handleServerHandlerWhiteBoardSignal = (data) => {
            const signal = data.data;
            safeSignalPeer(serverHandlerPeerRef.current, signal, "shareScreenHelper server-handler");
        };

        const handleConnectError = () => {
            if (!socketErroredRef.current) {
                setSocketErrored(true);
                socketErroredRef.current = true;
            }
        };

        const handleConnect = () => {
            if (socketErroredRef.current) {
                setSocketErrored(false);
                socketErroredRef.current = false;
            }
        };

        const handleTeacherStartedSessionWhiteBoard = () => {
            setSessionStarted(true);
        };

        const handleShareScreenCloseStreamToTeacher = () => {
            stopMediaStream(studentStreamRef.current);
            setStudentShareScreenStream(false);
        };

        const unbindEvents = bindSocketEvents(socket, [
            { event: "white-board-student-signal", handler: handleWhiteBoardStudentSignal },
            { event: "student-share-screen-signal-to-teacher", handler: handleStudentShareScreenSignalToTeacher },
            { event: "screen-sharing-dimentions", handler: handleScreenSharingDimensions },
            { event: "closed-sharing-stream", handler: handleClosedSharingStream },
            { event: "student-connected-white-board", handler: handleStudentConnectedWhiteBoard },
            { event: "student-ready-for-whiteboard", handler: handleStudentReadyForWhiteboard },
            { event: "student-disconnect", handler: handleStudentDisconnect },
            { event: "white-board-server-end-session", handler: handleWhiteBoardServerEndSession },
            { event: "server-handler-white-board-signal", handler: handleServerHandlerWhiteBoardSignal },
            { event: "connect_error", handler: handleConnectError },
            { event: "connect", handler: handleConnect },
            { event: "teacher-started-session-white-board", handler: handleTeacherStartedSessionWhiteBoard },
            { event: "share-screen-close-stream-to-teacher", handler: handleShareScreenCloseStreamToTeacher }
        ]);

        return () => {
            unbindEvents();
            whiteboardReadyTimersRef.current.forEach(clearTimeout);
            whiteboardReadyTimersRef.current.clear();
            tutorPeerRefs.current = destroyPeerCollection(tutorPeerRefs.current, "shareScreenHelper listener cleanup");
        };
    }, [scheduleConnectedStudent])

    useEffect(() => {
        if (socketErrored && (screenSharingDimensions || screenSharingDimensionsRef.current)) {
            handleCloseSharingScreen();
            return;
        }
        if ((screenSharingDimensions || screenSharingDimensionsRef.current) && sessionStarted && !socketErrored) {
            socket.emit("kill-white-board-stream-server-handler", {})
            stopMediaStream(stream);
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
            setStream(null)
            tutorSreamRef.current = null;
            return
        }
        if (socketErrored) {
            if (stream) {
                socket.emit("kill-white-board-stream-server-handler", {})
                tutorPeerRefs.current = destroyPeerCollection(tutorPeerRefs.current, "shareScreenHelper socket-errored");
                destroyPeer(serverHandlerPeerRef.current, "shareScreenHelper socket-errored server-handler");
                serverHandlerPeerRef.current = null
            }
            return
        }
        if (sessionStarted && !socketErrored) {
            if (stream) {
                tutorPeerRefs.current = destroyPeerCollection(tutorPeerRefs.current, "shareScreenHelper session-started");
                destroyPeer(serverHandlerPeerRef.current, "shareScreenHelper session-started server-handler");
                serverHandlerPeerRef.current = null
                socket.emit("get-server-handler", serverHandler => {
                    if (serverHandler) {
                        createServerHandlerPeer(serverHandler, stream);
                        socket.emit("get-connected-students", students => {
                            if (students.length) {
                                students.map(student => {
                                    if (student.user_type === 0)
                                        createPeer(student, stream);
                                })
                            }
                        })
                    }
                })
            } else {
                if (!screenSharingDimensions && !screenSharingDimensionsRef.current) {
                    handleWhiteBoardStream()
                }
            }

        }
        return () => {
            tutorPeerRefs.current = destroyPeerCollection(tutorPeerRefs.current, "shareScreenHelper session effect cleanup");
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
            tutorSreamRef.current = null;
        }
    }, [sessionStarted, stream, socketErrored, screenSharingDimensions])
    useEffect(() => {
        if (meetingStarted) {
            tutorPeerRefs.current = destroyPeerCollection(tutorPeerRefs.current, "shareScreenHelper meeting-started");
            meetingStartedRef.current = true;
        } else {
            meetingStartedRef.current = false;
            if (stream && !socketErrored) {
                const allTracksEnded = stream.getTracks().every(track => track.readyState === 'ended');
                if (!allTracksEnded) {
                    tutorPeerRefs.current = destroyPeerCollection(tutorPeerRefs.current, "shareScreenHelper meeting-resume");
                    destroyPeer(serverHandlerPeerRef.current, "shareScreenHelper meeting-resume server-handler");
                    serverHandlerPeerRef.current = null;

                    socket.emit("get-server-handler", serverHandler => {
                        if (serverHandler) {
                            createServerHandlerPeer(serverHandler, stream);
                            socket.emit("get-connected-students", students => {
                                if (students.length) {
                                    students.map(student => {
                                        if (student.user_type === 0) createPeer(student, stream);
                                    })
                                }
                            })
                        }
                    })
                }
            }
        }
    }, [meetingStarted, socketErrored])
    const handleWhiteBoardStream = async () => {
        let captureError = null;
        try {
            if (!window.electron || typeof window.electron.getTabSourceId !== 'function') {
                throw new Error("Electron tab source API is unavailable.");
            }
            const sourceId = await window.electron.getTabSourceId();
            if (!sourceId) {
                throw new Error("Electron tab source API returned an empty source id.");
            }
            const str = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: {
                    mandatory: {
                        chromeMediaSource: 'tab',
                        chromeMediaSourceId: sourceId,
                    }
                }
            });
            tutorSreamRef.current = str;
            setStream(str)
        } catch (e) {
            captureError = e;
            console.log("[shareScreenHelper] Tab capture failed, trying display capture:", e.message)
        }

        if (!captureError) return;

        if (!navigator.mediaDevices?.getDisplayMedia) {
            console.log("[shareScreenHelper] Stream error:", captureError.message)
            return;
        }

        try {
            const str = await navigator.mediaDevices.getDisplayMedia({
                audio: false,
                video: {
                    frameRate: { ideal: 10, max: 15 }
                }
            });
            tutorSreamRef.current = str;
            setStream(str)
            console.log("[shareScreenHelper] Display capture fallback started.")
        } catch (e) {
            console.log("[shareScreenHelper] Stream error:", e.message)
        }
    }

    const handleCloseSharingScreen = () => {
        // Check if electron API exists before calling
        if (window.electron && typeof window.electron.closeShareScreen === 'function') {
            window.electron.closeShareScreen();
        }
        screenSharingDimensionsRef.current = false;
        setScreenSharingDimensions(false)
    }

    useEffect(() => {
        if (sessionEnded) {
            tutorPeerRefs.current = destroyPeerCollection(tutorPeerRefs.current, "shareScreenHelper session-ended");
            stopMediaStream(stream);

            setStream(null)
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
            tutorSreamRef.current = null;
        }
    }, [sessionEnded])
    const createPeer = useCallback((student, stream) => {
        const currentWhiteBoardDimensions = whiteBoardDimensionsRef.current;
        if (!student?.socket_id || !stream || !stream.active || !currentWhiteBoardDimensions) {
            return;
        }
        const newPeer = new Peer({
            initiator: true,
            trickle: true,
            stream,
            config: getIceConfig()
        });
        newPeer.on("signal", (data) => {
            socket.emit(SOCKET_EVENTS.LEGACY_WHITEBOARD_TUTOR_SIGNAL, { signal: data, tutor_id: socket.id, studentSocketId: student.socket_id, whiteBoardDimensions: currentWhiteBoardDimensions });
        });

        newPeer.on('error', (err) => {
            // createPeer(student, stream);
            console.log("the peer error is ", err)

        })
        const studentAdded = tutorPeerRefs.current.find(p => p.studentSocketId === student.socket_id);
        if (studentAdded) {
            destroyPeer(studentAdded.peer, "shareScreenHelper replace student-peer");
            studentAdded.peer = newPeer;
            studentAdded.createdAt = Date.now();
        } else {
            tutorPeerRefs.current.push({ peer: newPeer, studentSocketId: student.socket_id, createdAt: Date.now() });
        }
    }, []);


    const createServerHandlerPeer = useCallback((serverHandler, stream) => {
        const currentWhiteBoardDimensions = whiteBoardDimensionsRef.current;
        if (!serverHandler?.socket_id || !stream || !stream.active || !currentWhiteBoardDimensions) {
            return;
        }
        destroyPeer(serverHandlerPeerRef.current, "shareScreenHelper createServerHandlerPeer");
        const newPeer = new Peer({
            initiator: true,
            trickle: true,
            stream,
            config: getIceConfig()
        });
        newPeer.on("signal", (data) => {
            socket.emit("white-board-signal-to-server-handler", { signal: data, tutor_id: socket.id, handlerSocketId: serverHandler.socket_id, whiteBoardDimensions: currentWhiteBoardDimensions });
        });
        newPeer.on('error', (err) => {
            // createServerHandlerPeer(serverHandler, stream);
            console.log("the peer error is ", err)
        })
        serverHandlerPeerRef.current = newPeer
    }, [])

    useEffect(() => {
        const video = studentVideoRef.current;
        if (video && studentShareScreenStream) {
            video.srcObject = studentShareScreenStream;
            video.play().catch(() => {});
        }
    }, [whiteBoardDimensions, studentShareScreenStream])

    return (
        <>
            <Snackbar
                open={socketErrored}
                autoHideDuration={5000}
                message={t('socketErrored') ?? ('error500')}
                action={
                    <Button color="primary" size="small" onClick={null} sx={{ m: '0px 20px' }}>
                        {t("ok")}
                    </Button>}

            />
            <video ref={videoRef} autoPlay muted style={{ display: 'none' }} />
            {whiteBoardDimensions && studentShareScreenStream &&
                <video data-testid="student-screen-share-video" ref={studentVideoRef} autoPlay muted
                    style={{
                        position: 'absolute',
                        insetBlockStart: '42px',
                        insetBlockEnd: 0,
                        insetInlineStart: '52px',
                        insetInlineEnd: 0,
                        width: 'calc(100% - 52px)',
                        height: 'calc(100% - 42px)',
                        objectFit: 'contain',
                        backgroundColor: '#000',
                        zIndex: 10,
                    }} />}
        </>
    )
}

export default ShareScreenHelper





